import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type {
  TeamPositionEntryPublic,
  TeamPlayerPublic,
  SportPositions,
  PositionDef,
  TeamPositionAction,
} from "@dugads/shared";
import { getPositionsForSport, getPositionByNumber } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { PositionCard } from "@/components/position-card";
import { ArrowLeft, ChevronDown, ChevronRight, Search, Trash2, User } from "lucide-react";

type FilterMode = "all" | "allocated" | "unallocated";

export default function TeamPositionsPage() {
  const { teamId } = useParams<{ teamId: string }>();

  const [teamName, setTeamName] = useState("");
  const [sportName, setSportName] = useState<string | null>(null);
  const [positions, setPositions] = useState<SportPositions | null>(null);
  const [roster, setRoster] = useState<TeamPlayerPublic[]>([]);
  const [positionData, setPositionData] = useState<TeamPositionEntryPublic[]>([]);
  const [actions, setActions] = useState<TeamPositionAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activePlayerId, setActivePlayerId] = useState<string | null>(null);
  const [playerSearch, setPlayerSearch] = useState("");
  const [filter, setFilter] = useState<FilterMode>("all");
  const [startersOpen, setStartersOpen] = useState(true);

  const canEdit = actions.includes("edit");

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);

    try {
      const [posRes, rosterRes] = await Promise.all([
        api.getTeamPositions(teamId),
        api.getTeamPlayers(teamId),
      ]);

      setTeamName(posRes.team_name);
      setSportName(posRes.sport_name);
      setRoster(rosterRes.players);
      setActions(posRes._actions);
      setPositionData(posRes.positions);

      const sport = posRes.sport_name
        ? getPositionsForSport(posRes.sport_name)
        : undefined;

      if (sport) {
        setPositions(sport);
      } else {
        setPositions(null);
        setError(
          posRes.sport_name
            ? `No position layout defined for "${posRes.sport_name}"`
            : "Team has no sport set. Edit the team to assign a sport first.",
        );
      }
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load positions");
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  // Derived data
  const allocatedPlayerIds = new Set(positionData.map((p) => p.player_id));
  const activeEntry = activePlayerId
    ? positionData.find((p) => p.player_id === activePlayerId) ?? null
    : null;

  // Get the player who occupies a given jersey number as primary
  function getPlayerAtPosition(jerseyNumber: number): TeamPositionEntryPublic | undefined {
    return positionData.find((p) => p.jersey_number === jerseyNumber);
  }

  // Get all players who can play a given position (primary first, then secondary)
  function getPlayersForPosition(jerseyNumber: number): { entry: TeamPositionEntryPublic; isPrimary: boolean }[] {
    const result: { entry: TeamPositionEntryPublic; isPrimary: boolean }[] = [];
    for (const entry of positionData) {
      if (entry.jersey_number === jerseyNumber) {
        result.unshift({ entry, isPrimary: true });
      } else if (entry.covers_positions.includes(jerseyNumber)) {
        result.push({ entry, isPrimary: false });
      }
    }
    return result;
  }

  // Filtered roster
  const filteredRoster = roster.filter((p) => {
    if (playerSearch && !p.player_name.toLowerCase().includes(playerSearch.toLowerCase())) {
      return false;
    }
    if (filter === "allocated") return allocatedPlayerIds.has(p.player_id);
    if (filter === "unallocated") return !allocatedPlayerIds.has(p.player_id);
    return true;
  });

  // Deselect active player if they're no longer visible in the filtered list
  useEffect(() => {
    if (activePlayerId && !filteredRoster.some((p) => p.player_id === activePlayerId)) {
      setActivePlayerId(null);
    }
  }, [activePlayerId, filteredRoster]);

  // --- Actions ---

  async function handlePositionClick(jerseyNumber: number) {
    if (!teamId || !activePlayerId || saving) return;

    setSaving(true);
    setError(null);
    try {
      if (!activeEntry) {
        // Player has no primary -- set this as primary
        const res = await api.setPlayerPosition(teamId, activePlayerId, {
          jersey_number: jerseyNumber,
          is_captain: false,
          is_vice_captain: false,
          secondary_positions: [],
        });
        setPositionData(res.positions);
      } else if (activeEntry.jersey_number === jerseyNumber) {
        // Clicking current primary -- remove entirely
        const res = await api.deletePlayerPosition(teamId, activePlayerId);
        setPositionData(res.positions);
      } else {
        // Toggle secondary
        const current = activeEntry.covers_positions;
        const newSecondary = current.includes(jerseyNumber)
          ? current.filter((p) => p !== jerseyNumber)
          : [...current, jerseyNumber].sort((a, b) => a - b);
        const res = await api.setPlayerPosition(teamId, activePlayerId, {
          jersey_number: activeEntry.jersey_number,
          is_captain: activeEntry.is_captain,
          is_vice_captain: activeEntry.is_vice_captain,
          secondary_positions: newSecondary,
        });
        setPositionData(res.positions);
      }
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to update position");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleCaptain() {
    if (!teamId || !activePlayerId || !activeEntry || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.setPlayerPosition(teamId, activePlayerId, {
        jersey_number: activeEntry.jersey_number,
        is_captain: !activeEntry.is_captain,
        is_vice_captain: activeEntry.is_captain ? activeEntry.is_vice_captain : false,
        secondary_positions: activeEntry.covers_positions,
      });
      setPositionData(res.positions);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to update captain");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleVC() {
    if (!teamId || !activePlayerId || !activeEntry || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.setPlayerPosition(teamId, activePlayerId, {
        jersey_number: activeEntry.jersey_number,
        is_captain: activeEntry.is_vice_captain ? activeEntry.is_captain : false,
        is_vice_captain: !activeEntry.is_vice_captain,
        secondary_positions: activeEntry.covers_positions,
      });
      setPositionData(res.positions);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to update vice-captain");
    } finally {
      setSaving(false);
    }
  }

  async function handleClearAll() {
    if (!teamId || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteTeamPositions(teamId);
      setPositionData([]);
      setActivePlayerId(null);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to clear positions");
    } finally {
      setSaving(false);
    }
  }

  // --- Render ---

  if (loading) {
    return <p className="text-muted-foreground" data-page="team-positions" data-state="loading">Loading...</p>;
  }

  if (!positions) {
    return (
      <div className="flex flex-col gap-4" data-page="team-positions" data-state="error" data-error>
        <div className="flex items-center gap-3">
          <Link
            to={`/teams/${teamId}`}
            className={buttonVariants({ variant: "ghost", size: "sm" })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold">Position Profiles</h1>
            <p className="text-muted-foreground">{teamName}</p>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    );
  }

  // Build formation rows for starters
  const rows = new Map<number, { pos: PositionDef }[]>();
  for (const pos of positions.positions) {
    const arr = rows.get(pos.row) ?? [];
    arr.push({ pos });
    rows.set(pos.row, arr);
  }
  for (const arr of rows.values()) {
    arr.sort((a, b) => a.pos.col - b.pos.col);
  }
  const sortedRows = [...rows.entries()].sort((a, b) => a[0] - b[0]);

  const starterCount = positionData.filter((p) => p.jersey_number <= positions.starters).length;

  return (
    <div className="flex flex-col gap-6" data-page="team-positions" data-state="ready">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link
            to={`/teams/${teamId}`}
            className={buttonVariants({ variant: "ghost", size: "sm" })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold">Position Profiles</h1>
            <p className="text-muted-foreground">
              {teamName}
              {sportName && ` \u00b7 ${sportName}`}
            </p>
          </div>
        </div>
        {canEdit && positionData.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            disabled={saving}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Clear All
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Player Roster Sidebar */}
        {canEdit && (
          <div className="order-2 flex flex-col rounded-lg border border-border p-4 lg:order-1 lg:w-64 lg:shrink-0 lg:self-start lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)]" data-region="sidebar">
            <h2 className="mb-3 text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Roster ({roster.length})
            </h2>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search players..."
                value={playerSearch}
                onChange={(e) => setPlayerSearch(e.target.value)}
                className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <div className="mb-3 flex gap-1">
              {(["all", "allocated", "unallocated"] as FilterMode[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    filter === f
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-primary/10"
                  }`}
                >
                  {f === "all" ? "All" : f === "allocated" ? "Allocated" : "Not yet"}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-1.5 overflow-y-auto">
              {filteredRoster.map((p) => {
                const entry = positionData.find((e) => e.player_id === p.player_id);
                const isActive = activePlayerId === p.player_id;
                return (
                  <button
                    key={p.player_id}
                    type="button"
                    onClick={() =>
                      setActivePlayerId(isActive ? null : p.player_id)
                    }
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/50 hover:bg-muted"
                    }`}
                  >
                    <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate text-left font-medium">
                      {p.player_name}
                    </span>
                    {entry && (
                      <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-xs font-bold text-primary">
                        #{entry.jersey_number}
                        {entry.is_captain && " (c)"}
                        {entry.is_vice_captain && " (vc)"}
                      </span>
                    )}
                  </button>
                );
              })}
              {filteredRoster.length === 0 && (
                <p className="text-sm text-muted-foreground py-2">No players match</p>
              )}
            </div>
            {activePlayerId && (
              <div className="mt-3 shrink-0 border-t border-border pt-3">
                <p className="text-xs text-muted-foreground mb-2">
                  {activeEntry
                    ? "Click positions to toggle primary/secondary"
                    : "Click a position to set as primary"}
                </p>
                {activeEntry && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={handleToggleCaptain}
                      disabled={saving}
                      className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                        activeEntry.is_captain
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-primary/10"
                      }`}
                    >
                      Captain
                    </button>
                    <button
                      type="button"
                      onClick={handleToggleVC}
                      disabled={saving}
                      className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                        activeEntry.is_vice_captain
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-primary/10"
                      }`}
                    >
                      Vice-Captain
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Formation + Reserves */}
        <div className="order-1 flex min-w-0 flex-1 flex-col gap-6 lg:order-2">
          {/* Formation Field */}
          <div className="rounded-lg border border-border bg-green-900/10 dark:bg-green-900/20" data-region="formation">
            <button
              type="button"
              onClick={() => setStartersOpen((v) => !v)}
              className="flex w-full items-center gap-2 p-4 text-left"
            >
              {startersOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Starting XV ({starterCount}/{positions.starters})
              </h2>
            </button>
            {startersOpen && (
              <div className="flex flex-col items-center gap-3 px-4 pb-4">
                {sortedRows.map(([rowIdx, rowItems]) => (
                  <div
                    key={rowIdx}
                    className="flex flex-wrap justify-center gap-2"
                  >
                    {rowItems.map(({ pos }) => (
                      <PositionCard
                        key={pos.number}
                        jerseyNumber={pos.number}
                        position={pos}
                        holder={getPlayerAtPosition(pos.number)}
                        playersForPosition={getPlayersForPosition(pos.number)}
                        activeEntry={activeEntry}
                        activePlayerId={activePlayerId}
                        canEdit={canEdit}
                        saving={saving}
                        onClick={() => handlePositionClick(pos.number)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
